// Payroll Settings → "Pay Schedule": the per-legal-entity pay day and cutoffs —
// the dates that power the payroll layer of the Workforce Calendar (employees
// see their pay day; payroll staff see the cutoffs). Day-of-month rules only;
// concrete dates are derived server-side (clamped to month end, weekends
// shifted to Friday when enabled). A live preview shows the next occurrences.
import React, { useEffect, useMemo, useState } from 'react';
import { CalendarClock, CheckCircle2, Loader2, Save } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import {
  listLegalEntities, getPaySchedule, setPaySchedule,
  type LegalEntity, type PaySchedule,
} from '@/services/payrollSettingsService';
import { getApiErrorMessage } from '@/lib/apiError';

const fieldCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';
const labelCls = 'text-xs font-medium text-muted-foreground';

// Mirror of the server's date rule, for the live preview only (the server
// remains the source of truth): clamp to month end, shift Sat/Sun → Friday.
function resolveDay(year: number, month: number, day: number, adjustWeekends: boolean): Date {
  const last = new Date(year, month + 1, 0).getDate();
  let d = new Date(year, month, Math.min(day, last));
  if (adjustWeekends) {
    if (d.getDay() === 6) d = new Date(year, month, d.getDate() - 1);
    else if (d.getDay() === 0) d = new Date(year, month, d.getDate() - 2);
  }
  return d;
}

export const PayScheduleTab: React.FC = () => {
  const [entities, setEntities] = useState<LegalEntity[]>([]);
  const [entityId, setEntityId] = useState('');

  useEffect(() => {
    listLegalEntities().then((l) => { setEntities(l); if (l[0]) setEntityId((c) => c || l[0].id); }).catch(() => {});
  }, []);

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-5 space-y-2">
          <h2 className="text-lg font-medium text-foreground flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-primary-500" /> Pay Schedule
          </h2>
          <p className="text-sm text-muted-foreground">
            When people get paid, and the cutoffs before it — per legal entity. These dates power the
            calendar: employees see their <strong>pay day</strong>; payroll staff also see the cutoffs.
          </p>
          <label className="space-y-1.5 block max-w-md pt-1">
            <span className={labelCls}>Legal entity</span>
            <select className={fieldCls} value={entityId} onChange={(e) => setEntityId(e.target.value)}>
              {entities.map((en) => <option key={en.id} value={en.id}>{en.name} ({en.country})</option>)}
            </select>
          </label>
        </CardContent>
      </Card>

      {entityId && <ScheduleEditor key={entityId} entityId={entityId} />}
    </div>
  );
};

const ScheduleEditor: React.FC<{ entityId: string }> = ({ entityId }) => {
  const [sched, setSched] = useState<PaySchedule | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getPaySchedule(entityId).then(setSched).catch((e) => setError(getApiErrorMessage(e, 'Could not load the schedule')));
  }, [entityId]);

  const preview = useMemo(() => {
    if (!sched) return [];
    const out: { label: string; date: Date }[] = [];
    const now = new Date();
    for (let i = 0; i < 3; i++) {
      const m = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const pay = resolveDay(m.getFullYear(), m.getMonth(), sched.pay_day, sched.adjust_weekends);
      if (pay >= now || i > 0) out.push({ label: 'Pay day', date: pay });
    }
    return out.slice(0, 3);
  }, [sched]);

  const save = async () => {
    if (!sched) return;
    setBusy(true); setError(null); setSaved(false);
    try {
      const updated = await setPaySchedule(entityId, sched);
      setSched(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not save the schedule'));
    } finally {
      setBusy(false);
    }
  };

  if (!sched) {
    return error
      ? <p className="text-sm text-danger">{error}</p>
      : <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }

  const dayField = (label: string, hint: string, key: 'pay_day' | 'attendance_cutoff_day' | 'input_cutoff_day') => (
    <label className="space-y-1.5 block">
      <span className={labelCls}>{label}</span>
      <input
        type="number" min={1} max={31}
        className={fieldCls}
        value={sched[key]}
        onChange={(e) => setSched({ ...sched, [key]: Number(e.target.value) })}
      />
      <span className="block text-[11px] text-muted-foreground">{hint}</span>
    </label>
  );

  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-foreground m-0">
            Monthly schedule {sched.version === 0 && <span className="text-xs text-muted-foreground">(defaults — not saved yet)</span>}
          </p>
          {saved && (
            <span className="flex items-center gap-1.5 text-sm text-success-strong">
              <CheckCircle2 className="h-4 w-4" /> Saved
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
          {dayField('Attendance cutoff day', 'Attendance/timesheets close for the period.', 'attendance_cutoff_day')}
          {dayField('Inputs cutoff day', 'Bonuses, deductions and other variable inputs close.', 'input_cutoff_day')}
          {dayField('Pay day', 'Day of month people get paid. 31 = last day of the month.', 'pay_day')}
        </div>

        <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={sched.adjust_weekends}
            onChange={(e) => setSched({ ...sched, adjust_weekends: e.target.checked })}
            className="h-4 w-4 rounded border-border accent-orange-600"
          />
          Move weekend dates to the preceding Friday
        </label>

        {preview.length > 0 && (
          <div className="rounded-md bg-muted/60 p-3 text-sm">
            <span className="text-muted-foreground">Next pay days: </span>
            {preview.map((p, i) => (
              <span key={i} className="font-medium text-foreground">
                {p.date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                {i < preview.length - 1 ? ' · ' : ''}
              </span>
            ))}
          </div>
        )}

        {error && <p className="text-sm text-danger m-0">{error}</p>}

        <Button onClick={save} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save schedule
        </Button>
      </CardContent>
    </Card>
  );
};

export default PayScheduleTab;
